import { useTimezone } from '@app/core/hooks/useTimezone';
import { ListView } from '@app/core/list-view/ListView';
import { ListViewQueryParams } from '@app/core/list-view/types';
import { Station } from '@app/core/types/stations';
import { crudListToPaginated, toCrudSort } from '@app/modules/audit/utils/audit-matches-crud';
import { auditSpotAuditSpotMediaList } from '@mi-front/api-v2';
import { AuditAudio, Paginated } from '@mi-front/core';
import { AUDIOS_LIST_FILTERS_CONFIG } from './audios-filters-config';
import { audiosListFiltersToApi, audiosListSortToApi } from './map-audios-list-query';
import { AudiosListResult } from './AudiosListResult';

interface Props {
  readonly station: Station;
}

/**
 * Station-scoped audios listing via api-v2 media list (ListView filters).
 */
export function AudiosListView({ station }: Props) {
  const timezone = useTimezone();

  const queryFn = async (params: ListViewQueryParams): Promise<Paginated<AuditAudio>> => {
    const filter = audiosListFiltersToApi(params.filters, timezone);
    const customId = filter.customIdLike?.replace(/%/g, '');

    const res = await auditSpotAuditSpotMediaList({
      station: station.slug,
      filter: [
        ...(filter.from ? [`createdAt||$gte||${filter.from}`] : []),
        ...(filter.to ? [`createdAt||$lte||${filter.to}`] : []),
        ...(customId ? [`customId||$cont||${customId}`] : []),
      ],
      page: (params.pageIndex ?? 0) + 1,
      limit: params.pageSize,
      sort: toCrudSort(audiosListSortToApi(params.sort)) ?? ['createdAt,DESC'],
    });

    // OpenAPI media list omits updatedAt / stationsGroupId / transcription present at runtime.
    return crudListToPaginated({
      ...res,
      data: res.data as unknown as AuditAudio[],
    });
  };

  return (
    <ListView
      listId={`audit-audios-list-${station.slug}`}
      filters={AUDIOS_LIST_FILTERS_CONFIG}
      queryFn={queryFn}
      ListComponent={AudiosListResult}
      stickyFilters={false}
    />
  );
}
